import React, { useState, useEffect } from 'react';
import './styles.css';
import { FaEye, FaTrash, FaUpload, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import configData from '../../configure';
import ClassDetails from './ClassDetails';

function Classes({ orgId }) {
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState('');
  const [sectionName, setSectionName] = useState(''); // Specific section name
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editClassName, setEditClassName] = useState('');
  const [editSectionName, setEditSectionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [courseDetailsCache, setCourseDetailsCache] = useState({});
  const [isViewScreen, setIsViewScreen] = useState(false);
  const [viewingClass, setViewingClass] = useState(null);
  const { axios, token, hasPermission, userRole, studentInfo } = useAuth();
  const navigate = useNavigate();

  // Bulk Upload State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ success: 0, fail: 0 });

  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  useEffect(() => {
    setClasses([]);
    setClassName('');
    setSectionName('');
    setIsEditModalOpen(false);
    setEditingClass(null);
    setEditClassName('');
    setEditSectionName('');
    setCourseDetailsCache({});
    setIsViewScreen(false);
    setViewingClass(null);
  }, [orgId]);

  const fetchCourseDetails = async (courseId) => {
    if (courseDetailsCache[courseId]) {
      return courseDetailsCache[courseId];
    }

    try {
      const response = await axios.get(`${configData.GET_COURSE_BY_ID}/${courseId}`, {
        headers: { Authorization: token },
      });

      const courseName = response.data.courseDetails?.courseName || response.data.courseName || 'Unknown Course';
      const courseCode = response.data.courseDetails?.courseCode || response.data.courseCode || '';

      const courseDetails = {
        name: courseName,
        code: courseCode,
        displayName: courseCode ? `${courseName} (${courseCode})` : courseName
      };

      setCourseDetailsCache(prev => ({
        ...prev,
        [courseId]: courseDetails
      }));

      return courseDetails;
    } catch (error) {
      console.error(`Error fetching course details for ${courseId}:`, error);
      return {
        name: 'Unknown Course',
        code: '',
        displayName: 'Unknown Course'
      };
    }
  };

  const getCourseDisplayNames = async (courseIds) => {
    if (!courseIds || courseIds.length === 0) return '';

    try {
      const courseDetails = await Promise.all(
        courseIds.map(courseId => fetchCourseDetails(courseId))
      );
      return courseDetails.map(detail => detail.displayName).join(', ');
    } catch (error) {
      console.error('Error fetching course names:', error);
      return 'Error loading courses';
    }
  };

  const fetchClasses = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}`, {
        headers: { Authorization: token },
      });

      let processedData = response.data.map((cls) => {
        return {
          ...cls,
          id: cls.classId || cls.id,
          courseNames: '' // Defer fetching course display names until details are viewed to eliminate N+1 cascade
        };
      });

      if (userRole === 'STUDENT' && studentInfo?.classId) {
        processedData = processedData.filter(cls => cls.id === studentInfo.classId);
      }

      setClasses(processedData);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, [token, orgId, axios]);

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setUploadStats({ success: 0, fail: 0 });
  };

  const downloadTemplate = () => {
    const csvContent = isCustomOrg ? "Class Name\nClass 10\nClass 9" : "Class Name,Section\nClass 10,A\nClass 10,B";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "classes_template.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const ioProcessCSV = (str) => {
    const rows = str.trim().split('\n');
    if (rows.length < 2) return [];
    return rows.slice(1).map(row => {
      const values = row.split(',');
      return {
        className: values[0] ? values[0].trim() : '',
        sectionName: isCustomOrg ? '' : (values[1] ? values[1].trim() : ''),
        courseIds: ["string"]
      };
    }).filter(row => row.className && (isCustomOrg || row.sectionName));
  };

  const handleBulkUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a file first.");
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const data = ioProcessCSV(text);

      let successCount = 0;
      let failCount = 0;

      for (const item of data) {
        try {
          // Optional: Check for local duplicates before sending
          const exists = classes.some(c => c.className === item.className && (isCustomOrg || c.sectionName === item.sectionName));
          if (exists) {
            console.log(`Skipping duplicate: ${item.className} ${item.sectionName}`);
            failCount++;
            continue;
          }

          await axios.post(configData.CREATE_CLASS, {
            className: item.className,
            sectionName: isCustomOrg ? '' : (item.sectionName || ''),
            orgId: orgId,
            courseIds: item.courseIds
          }, {
            headers: { Authorization: token },
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to create ${item.className} ${item.sectionName}`, err);
          failCount++;
        }
      }

      setUploadStats({ success: successCount, fail: failCount });
      setUploading(false);
      setCsvFile(null);
      toast.success(`Upload complete. Created: ${successCount}, Skipped/Failed: ${failCount}`);
      setIsUploadModalOpen(false);
      fetchClasses();
    };
    reader.readAsText(csvFile);
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (isCustomOrg) {
      if (!className.trim()) {
        toast.warning('Please enter class name.');
        return;
      }
    } else {
      if (!className.trim() || !sectionName.trim()) {
        toast.warning('Please enter both class name and section name.');
        return;
      }
    }

    const classData = {
      className: className.trim(),
      sectionName: isCustomOrg ? '' : sectionName.trim(),
      orgId: orgId,
      courseIds: ["string"],
    };

    try {
      const response = await axios.post(configData.CREATE_CLASS, classData, {
        headers: { Authorization: token },
      });
      const createdId = response.data.classId || response.data.id || Date.now();
      const newClass = {
        ...classData,
        classId: createdId,
        id: createdId,
        courseNames: ''
      };
      setClasses([...classes, newClass]);
      setClassName('');
      setSectionName('');
      toast.success('Class created successfully!');
    } catch (error) {
      console.error(`Error creating class: ${classData.className} ${classData.sectionName}`, error);
      toast.error(error.response?.data?.message || 'Error creating class. Please try again.');
    }
  };

  const handleEditClick = (cls) => {
    setEditingClass(cls);
    setEditClassName(cls.className);
    setEditSectionName(cls.sectionName || '');
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (isCustomOrg) {
      if (!editClassName.trim()) {
        toast.warning('Please enter class name.');
        return;
      }
    } else {
      if (!editClassName.trim() || !editSectionName.trim()) {
        toast.warning('Please enter both class name and section name.');
        return;
      }
    }

    const id = editingClass.classId || editingClass.id;
    const updatedData = {
      classId: id,
      className: editClassName.trim(),
      sectionName: isCustomOrg ? '' : editSectionName.trim(),
      orgId: orgId,
      courseIds: editingClass.courseIds || ["string"],
      academicYear: editingClass.academicYear || "2025-2026",
      classScheduleMap: editingClass.classScheduleMap || null,
      durationMins: editingClass.durationMins || 40
    };

    try {
      const response = await axios.put(`${configData.GET_CLASS_BY_ID}/${id}`, updatedData, {
        headers: { Authorization: token },
      });

      if (response.status === 200) {
        setClasses(classes.map(cls => (cls.classId || cls.id) === id ? { ...cls, ...updatedData } : cls));
        setIsEditModalOpen(false);
        setEditingClass(null);
        toast.success('Class updated successfully!');
      }
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error(error.response?.data?.message || 'Error updating class. Please try again.');
    }
  };

  const handleDelete = async (id, className, sectionName) => {
    if (!hasPermission('CLASSES', 'DELETE') && userRole !== 'ADMIN') {
      toast.error('Permission denied');
      return;
    }

    if (!id) {
      toast.error('Error: Class ID is missing');
      return;
    }

    const confirmDelete = window.confirm(
      isCustomOrg
        ? `Are you sure you want to delete ${className}?`
        : `Are you sure you want to delete ${className} Section ${sectionName}?`
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(`${configData.DELETE_CLASS_BY_ID}/${id}`, {
        headers: { Authorization: token },
      });

      if (response.status >= 200 && response.status < 300) {
        const updatedClasses = classes.filter(cls => (cls.classId || cls.id) !== id);
        setClasses(updatedClasses);
        toast.success(isCustomOrg ? `Successfully deleted ${className}` : `Successfully deleted ${className} Section ${sectionName}`);
      } else {
        toast.error('Failed to delete class. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error(error.response?.data?.message || 'Error deleting class. Please try again.');
    }
  };

  const handleView = (cls) => {
    setViewingClass(cls);
    setIsViewScreen(true);
  };

  const handleBackToList = () => {
    setIsViewScreen(false);
    setViewingClass(null);
  };

  const sortedClasses = classes
    .map(cls => ({
      ...cls,
      id: cls.classId || cls.id,
    }))
    .sort((a, b) => {
      const numA = parseInt(a.className.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.className.replace(/\D/g, '')) || 0;

      if (numA !== numB) {
        return numA - numB;
      } else if (a.className !== b.className) {
        return a.className.localeCompare(b.className);
      }

      return (a.sectionName || '').localeCompare(b.sectionName || '');
    });

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div className="clcontainer">
          <div className="loading-state">
            <p>Loading classes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="clcontainer">
        <div className="no-org-message">
          <p>Please select an organization to view classes.</p>
        </div>
      </div>
    );
  }

  if (isViewScreen && viewingClass) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <ClassDetails
          viewingClass={viewingClass}
          onBack={handleBackToList}
          orgId={orgId}
          token={token}
          axios={axios}
          courseDetailsCache={courseDetailsCache}
          setCourseDetailsCache={setCourseDetailsCache}
          setClasses={setClasses}
          classes={classes}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div className="clcontainer" key={orgId}>
      <div className="clform-section">
        <form className="clform-row" onSubmit={handleCreate}>
          <div className="clform-group">
            <label>Class Name</label>
            <input
              type="text"
              value={className}
              onChange={e => setClassName(e.target.value)}
              placeholder="e.g., Class 1"
            />
          </div>
          {!isCustomOrg && (
            <div className="clform-group">
              <label>Section Name</label>
              <input
                type="text"
                value={sectionName}
                onChange={e => setSectionName(e.target.value)}
                placeholder="e.g., A or Section 1"
              />
            </div>
          )}
          {userRole !== 'STUDENT' && (hasPermission('CLASSES', 'CREATE') || userRole === 'ADMIN') && (
            <>
              <div className="clform-button">
                <button type="submit">Create</button>
              </div>
              <div className="clform-button" style={{ marginLeft: '10px' }}>
                <button type="button" onClick={() => setIsUploadModalOpen(true)} style={{ backgroundColor: '#28a745' }}>
                  <FaUpload style={{ marginRight: '5px' }} /> Bulk Upload
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      <div className="cltable-section">
        <h2>{isCustomOrg ? 'Classes List' : 'Class Section List'}</h2>
        <div className="cltable-wrapper">
          <table>
            <thead>
              <tr>
                <th>Class Name</th>
                {!isCustomOrg && <th>Section</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedClasses.length === 0 ? (
                <tr>
                  <td colSpan={isCustomOrg ? "2" : "3"} className="no-data">
                    No classes found for this organization.
                  </td>
                </tr>
              ) : (
                sortedClasses.map((cls, index) => {
                  const isFirstOccurrence =
                    index === 0 || sortedClasses[index - 1].className !== cls.className;

                  return (
                    <tr key={cls.id || `${cls.className}-${cls.sectionName}`}>
                      <td>{isCustomOrg ? cls.className : (isFirstOccurrence ? cls.className : '')}</td>
                      {!isCustomOrg && <td>{cls.sectionName}</td>}
                      <td className="claction-buttons">
                        <button
                          className="clview-btn"
                          onClick={() => handleView(cls)}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        {userRole !== 'STUDENT' && (
                          <>
                            <button
                              className="cledit-btn"
                              onClick={() => handleEditClick(cls)}
                              title="Edit Name"
                              style={{ marginLeft: '8px', color: '#007bff' }}
                            >
                              <FaEdit />
                            </button>
                            {(hasPermission('CLASSES', 'DELETE') || userRole === 'ADMIN') && (
                              <button
                                className="cldelete-btn"
                                onClick={() => handleDelete(cls.classId || cls.id, cls.className, cls.sectionName)}
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isCustomOrg ? 'Edit Class' : 'Edit Class / Section'}</h3>
              <button className="modal-close-btn" onClick={() => setIsEditModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="clform-group" style={{ marginBottom: '15px' }}>
                  <label>Class Name</label>
                  <input
                    type="text"
                    value={editClassName}
                    onChange={e => setEditClassName(e.target.value)}
                    placeholder="e.g., Class 1"
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                {!isCustomOrg && (
                  <div className="clform-group">
                    <label>Section Name</label>
                    <input
                      type="text"
                      value={editSectionName}
                      onChange={e => setEditSectionName(e.target.value)}
                      placeholder="e.g., A"
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="save-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Bulk Upload Classes</h3>
              <button className="modal-close-btn" onClick={() => setIsUploadModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                {isCustomOrg
                  ? "Upload a CSV file to create multiple classes at once. The file must include Class Name column."
                  : "Upload a CSV file to create multiple classes at once. The file must include Class Name and Section columns."}
              </p>

              <div className="form-group">
                <div className="file-upload-wrapper">
                  <input type="file" accept=".csv" onChange={handleFileChange} />
                  <div className="file-upload-content">
                    <div className="upload-icon">
                      <FaUpload />
                    </div>
                    <div className="upload-text">
                      {csvFile ? csvFile.name : "Click or drag CSV file here"}
                    </div>
                    {!csvFile && <div className="upload-hint">Supported format: .csv</div>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={downloadTemplate}
                  className="save-btn"
                  style={{
                    backgroundColor: 'white',
                    color: '#2563eb',
                    border: '1px solid #2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Download Sample Template
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setIsUploadModalOpen(false)}>Cancel</button>
              <button
                className="save-btn"
                onClick={handleBulkUpload}
                disabled={uploading || !csvFile}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default Classes;